import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import { Card, Empty } from '../ui/Card.jsx';
import { SourceChips } from './SourceChip.jsx';
import { SurveySessionTimeline } from './SurveySessionTimeline.jsx';

/**
 * RespondentsTable — 受訪者名單（by phone / preview userId 聚合）
 *
 * 點 row 展開 → 顯示該 phone 的所有 session 時間軸
 */
export function RespondentsTable({ respondents, loading, avatarId, hostId, campaigns = [], onOpenSession }) {
    const { t } = useTranslation('survey');
    const [expanded, setExpanded] = useState(null); // group key

    if (loading && (!respondents || respondents.length === 0)) {
        return (
            <Card>
                <div className="text-sm text-ink-400 italic text-center py-6">{t('common:state.loading')}</div>
            </Card>
        );
    }
    if (!respondents || respondents.length === 0) {
        return (
            <Card>
                <Empty icon={Users} title={t('table.emptyTitle')} description={t('table.emptyDesc')} />
            </Card>
        );
    }

    const campMap = new Map(campaigns.map((c) => [c.id, c.name]));

    return (
        <Card padded={false}>
            <div className="relative">
            {/* loading 細條(filter 切換時,即使有舊資料也顯示)*/}
            {loading && (
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-accent-500 to-transparent animate-pulse z-10" />
            )}
            {/* loading 時舊資料半透明 + cursor wait,讓 user 知道在更新 */}
            <div className={loading ? 'opacity-50 transition-opacity pointer-events-none' : 'transition-opacity'}>
            <table className="w-full">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                        <th className="text-left px-4 py-2.5 font-medium">{t('table.colId')}</th>
                        <th className="text-left px-4 py-2.5 font-medium">{t('table.colSource')}</th>
                        <th className="text-left px-4 py-2.5 font-medium">{t('table.colCampaign')}</th>
                        <th className="text-right px-4 py-2.5 font-medium">{t('table.colCalls')}</th>
                        <th className="text-right px-4 py-2.5 font-medium">{t('table.colCompletion')}</th>
                        <th className="text-right px-4 py-2.5 font-medium">{t('table.colLastAnswered')}</th>
                        <th className="w-8"></th>
                    </tr>
                </thead>
                <tbody>
                    {respondents.map((r) => (
                        <RespondentRow
                            key={r.key}
                            respondent={r}
                            expanded={expanded === r.key}
                            onToggle={() => setExpanded((cur) => (cur === r.key ? null : r.key))}
                            avatarId={avatarId}
                            hostId={hostId}
                            campaigns={campaigns}
                            campMap={campMap}
                            onOpenSession={onOpenSession}
                        />
                    ))}
                </tbody>
            </table>
            </div>
            </div>
        </Card>
    );
}

function RespondentRow({ respondent: r, expanded, onToggle, avatarId, hostId, campaigns, campMap, onOpenSession }) {
    const { t } = useTranslation('survey');
    const last = r.lastSessionAt ? new Date(r.lastSessionAt) : null;
    const isPreview = r.source === 'preview' && !r.phone;
    const displayId = isPreview
        ? t('previewId', { id: (r.userId || r.key).slice(0, 8) })
        : (r.phone || r.userId || r.key);

    const campaignNames = (r.campaignIds || [])
        .map((id) => campMap.get(id) || id.slice(0, 6))
        .slice(0, 2);
    const overflowCamps = Math.max(0, (r.campaignIds || []).length - 2);

    const completed = r.completedSessions > 0 || (r.coverage >= r.totalQuestions);
    const completionPct = r.totalQuestions ? Math.round((r.coverage / r.totalQuestions) * 100) : 0;

    return (
        <>
            <tr
                className={[
                    'border-t border-cream-100 hover:bg-cream-50 cursor-pointer',
                    expanded ? 'bg-cream-50' : '',
                ].join(' ')}
                onClick={onToggle}
            >
                <td className="px-4 py-3 align-top tnum">
                    <div className={['text-sm font-medium', isPreview ? 'text-ink-500 italic' : 'text-ink-900'].join(' ')}>
                        {displayId}
                    </div>
                    {r.demographics && Object.keys(r.demographics).length > 0 && (
                        <div className="mono-caption truncate max-w-[180px]" title={JSON.stringify(r.demographics)}>
                            {[r.demographics.age, r.demographics.gender, r.demographics.district].filter(Boolean).join(' · ')}
                        </div>
                    )}
                </td>
                <td className="px-4 py-3 align-top">
                    <SourceChips sources={(r.sources && r.sources.length ? r.sources : (r.source ? [r.source] : ['preview']))} />
                </td>
                <td className="px-4 py-3 align-top">
                    {campaignNames.length === 0 ? (
                        <span className="text-xs text-ink-300">—</span>
                    ) : (
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {campaignNames.map((name, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 text-xs bg-accent-50 text-accent-700 rounded-full truncate max-w-[180px]" title={name}>
                                    {name}
                                </span>
                            ))}
                            {overflowCamps > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs bg-cream-200 text-ink-500 rounded-full">+{overflowCamps}</span>
                            )}
                        </div>
                    )}
                </td>
                <td className="px-4 py-3 align-top text-right tnum">
                    <div className="text-sm text-ink-900">{r.totalSessions}</div>
                    {r.completedSessions > 0 && r.completedSessions < r.totalSessions && (
                        <div className="mono-caption">{t('table.completedCount', { n: r.completedSessions })}</div>
                    )}
                </td>
                <td className="px-4 py-3 align-top text-right tnum">
                    <div className={['text-sm font-medium', completed ? 'text-emerald-600' : 'text-ink-900'].join(' ')}>
                        {r.coverage}/{r.totalQuestions}
                    </div>
                    <div className="mono-caption">{completionPct}%</div>
                </td>
                <td className="px-4 py-3 align-top text-right">
                    <div className="text-sm text-ink-900 tnum">
                        {last ? last.toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' }) : '—'}
                    </div>
                    <div className="mono-caption">
                        {last ? last.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                </td>
                <td className="px-4 py-3 align-top">
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggle(); }}
                        className="inline-flex p-1.5 rounded hover:bg-cream-200 text-ink-400 transition"
                    >
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr className="bg-cream-100/50 border-t border-cream-200">
                    <td colSpan={7} className="px-6 py-5">
                        <SurveySessionTimeline
                            avatarId={avatarId}
                            hostId={hostId}
                            phone={r.phone}
                            groupKey={r.key}
                            campaigns={campaigns}
                            onOpenSession={onOpenSession}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}
